import React, { useEffect, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Table,
  Button,
} from "reactstrap"
import {
  get_user_devices,
  get_user_primary_device_history,
  add_user_primary_device,
  delete_user_primary_device,
} from "helpers/backend_helper"

const formatDate = value => (value ? String(value).substring(0, 10) : "")

// Admin modal to manage a user's effective-dated primary device history.
// Adding a new primary requires a start date; the API auto-closes the
// previous open record to the day before that date.
const PrimaryDeviceModal = ({ show, toggle, userId, onChanged }) => {
  const [history, setHistory] = useState([])
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const [deviceId, setDeviceId] = useState("")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  const loadData = async () => {
    if (!userId) return
    setLoading(true)
    setError("")
    try {
      const [hist, devs] = await Promise.all([
        get_user_primary_device_history(userId),
        get_user_devices(userId),
      ])
      setHistory(hist?.data || [])
      setDevices(devs?.data || [])
    } catch (err) {
      setError("Failed to load primary device history.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show) {
      setDeviceId("")
      setStartDate("")
      setEndDate("")
      setError("")
      loadData()
    }
  }, [show, userId])

  const handleAdd = async () => {
    if (!startDate) {
      setError("Start date is required.")
      return
    }
    if (endDate && endDate < startDate) {
      setError("End date cannot be before start date.")
      return
    }
    setSaving(true)
    setError("")
    try {
      const res = await add_user_primary_device({
        userid: userId,
        trackingDeviceId: deviceId ? Number(deviceId) : null,
        startdate: startDate,
        enddate: endDate || null,
      })
      if (res?.status === "Success!" || res?.statusCode === 200) {
        setDeviceId("")
        setStartDate("")
        setEndDate("")
        await loadData()
        if (onChanged) onChanged()
      } else {
        setError(res?.message || "Failed to add primary device.")
      }
    } catch (err) {
      setError("Failed to add primary device.")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async id => {
    setSaving(true)
    setError("")
    try {
      await delete_user_primary_device(id)
      await loadData()
      if (onChanged) onChanged()
    } catch (err) {
      setError("Failed to delete record.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen={show} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h5">
        Manage Primary Device
      </ModalHeader>
      <ModalBody>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}

        <div className="mb-3">
          <h6 className="mb-2">History</h6>
          {loading ? (
            <div className="text-muted">Loading…</div>
          ) : history.length === 0 ? (
            <div className="text-muted">No primary device records yet.</div>
          ) : (
            <Table bordered responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Current</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map(row => (
                  <tr key={row.id}>
                    <td>{row.deviceName || row.deviceUuid || "None"}</td>
                    <td>{formatDate(row.startdate)}</td>
                    <td>{row.enddate ? formatDate(row.enddate) : "—"}</td>
                    <td>{row.isCurrent ? "Yes" : ""}</td>
                    <td>
                      <Button
                        color="link"
                        className="text-danger p-0"
                        onClick={() => handleDelete(row.id)}
                        disabled={saving}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        <hr />

        <h6 className="mb-2">Add / Replace Primary Device</h6>
        <div className="row">
          <div className="col-md-4 mb-3">
            <Label className="form-label">Device</Label>
            <Input
              type="select"
              value={deviceId}
              onChange={e => setDeviceId(e.target.value)}
            >
              <option value="">None</option>
              {devices.map(d => (
                <option key={d.trackingDeviceId} value={d.trackingDeviceId}>
                  {d.deviceName} {d.ipAddress ? `(${d.ipAddress})` : ""}
                </option>
              ))}
            </Input>
          </div>
          <div className="col-md-4 mb-3">
            <Label className="form-label">
              Start Date <span className="text-danger">*</span>
            </Label>
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-4 mb-3">
            <Label className="form-label">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </div>
        </div>
        <div className="text-muted small">
          Leave End Date empty to make this the current primary device. Adding a
          new record automatically ends the previous one the day before the
          start date.
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="btn btn-primary me-2"
          onClick={handleAdd}
          disabled={saving}
        >
          {saving ? "Saving…" : "Add"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={toggle}>
          Close
        </button>
      </ModalFooter>
    </Modal>
  )
}

export default PrimaryDeviceModal
